import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Sparkles as AutoAwesomeIcon,
  FileText as DescriptionIcon,
  RotateCw as RefreshIcon,
  CheckCircle2 as CheckCircleIcon,
  Clock as ScheduleIcon
} from 'lucide-react';

import api from '../../../utils/axios';

const GenerateStep = ({ appeal, onNext, onBack, onUpdate, onError, onSuccess }) => {
  const [generating, setGenerating] = useState(false);
  const [generationComplete, setGenerationComplete] = useState(false);
  const [selectedModel, setSelectedModel] = useState('gemini-2.5-flash');
  const [jobStatus, setJobStatus] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [generatedLetter, setGeneratedLetter] = useState(null);
  const [generationAttempts, setGenerationAttempts] = useState(0);

  const availableModels = [
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (Recommended)', description: 'Fast and efficient Google AI model for medical appeals' },
    { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro', description: 'Advanced reasoning model for complex appeals' },
    { value: 'gemini-pro', label: 'Gemini Pro', description: 'High-quality general purpose model' }
  ];

  useEffect(() => {
    if (appeal) {
      // Check if letter already exists
      if (appeal.letter && appeal.letter.content) {
        setGeneratedLetter(appeal.letter);
        setGenerationComplete(true);
      }
    }
  }, [appeal]);

  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const startGeneration = async () => {
    if (!appeal || !appeal.id) {
      onError('Appeal not found');
      return;
    }

    // Validate that we have the necessary information
    if (!appeal.patient_name && !appeal.denial_text) {
      onError('Please complete the extraction step first to provide patient name and denial information');
      return;
    }

    setGenerating(true);
    setGenerationComplete(false);
    setJobStatus('starting');
    onError(null);

    try {
      const response = await api.post(`/documents/minimal-appeals/${appeal.id}/generate/`, {
        model: selectedModel
      });

      setJobStatus('queued');
      setGenerationAttempts(prev => prev + 1);
      onSuccess('Appeal generation started. This may take a few minutes...');

      // Start polling for results
      const interval = setInterval(async () => {
        await checkGenerationStatus();
      }, 5000);

      setPollingInterval(interval);

    } catch (err) {
      setGenerating(false);
      setJobStatus('failed');
      onError(err.response?.data?.error || 'Failed to start generation');
    }
  };

  const checkGenerationStatus = async () => {
    try {
      // Check for the generated letter
      const letterResponse = await api.get(`/documents/minimal-appeals/${appeal.id}/letter/`);

      if (letterResponse.data && letterResponse.data.content) {
        setGenerating(false);
        setGenerationComplete(true);
        setJobStatus('completed');
        setGeneratedLetter(letterResponse.data);

        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }

        // Update the appeal data
        const updatedAppeal = await api.get(`/documents/minimal-appeals/${appeal.id}/`);
        onUpdate(updatedAppeal.data);

        onSuccess('Appeal letter generated successfully! You can now review and edit it.');
      }
    } catch (err) {
      // If we get a 404, the letter isn't ready yet, continue polling
      if (err.response?.status !== 404) {
        console.error('Error checking generation status:', err);
        // Don't stop polling for other errors, as generation might still be in progress
      }
    }
  };

  const handleNext = () => {
    if (!generationComplete) {
      onError('Please wait for the appeal generation to complete');
      return;
    }
    onNext();
  };

  const renderAppealSummary = () => {
    if (!appeal) return null;

    const summaryData = [
      { label: 'Patient Name', value: appeal.patient_name || 'Not provided' },
      { label: 'Insurance Payer', value: appeal.payer_name || 'Not provided' },
      { label: 'Payer ID', value: appeal.payer_id || 'Not provided' },
      { label: 'Claim Number', value: appeal.claim_number || 'Not provided' },
      { label: 'Denial Code', value: appeal.denial_code || 'Not provided' },
      { label: 'Denial Reason', value: appeal.denial_text || 'Not provided', multiline: true },
      { label: 'Provider Phone', value: appeal.provider_phone || 'Not provided' },
      { label: 'Provider Address', value: appeal.provider_address || 'Not provided', multiline: true },
      { label: 'Documents', value: `${appeal.documents?.length || 0} files uploaded` }
    ];

    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Appeal Information Summary
          </Typography>
          <List dense>
            {summaryData.map((item, index) => (
              <ListItem key={index} sx={{ px: 0 }}>
                <ListItemText
                  primary={item.label}
                  secondary={item.multiline ? (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {item.value}
                    </Typography>
                  ) : item.value}
                  sx={{
                    '& .MuiListItemText-primary': { fontWeight: 500 },
                    '& .MuiListItemText-secondary': item.multiline ? { whiteSpace: 'pre-wrap' } : {}
                  }}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  };

  const renderModelSelection = () => {
    if (generating || generationComplete) return null;

    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            AI Model Selection
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Select AI Model</InputLabel>
            <Select
              value={selectedModel}
              label="Select AI Model"
              onChange={(e) => setSelectedModel(e.target.value)}
            >
              {availableModels.map((model) => (
                <MenuItem key={model.value} value={model.value}>
                  <Box>
                    <Typography variant="body1">{model.label}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {model.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>
    );
  };

  const renderGenerationInterface = () => {
    if (generating) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Generating Appeal Letter...
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Our AI is analyzing your documents and crafting a professional appeal letter.
              This process typically takes 2-5 minutes.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <Chip
                icon={<ScheduleIcon size={16} />}
                label={jobStatus === 'starting' ? 'Starting...' : 'Processing...'}
                variant="outlined"
                color="primary"
              />
            </Box>

            <LinearProgress sx={{ maxWidth: 400, mx: 'auto' }} />

            {generationAttempts > 0 && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                Generation attempt #{generationAttempts}
              </Typography>
            )}
          </CardContent>
        </Card>
      );
    }

    if (generationComplete) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon size={60} color="var(--ab-approved)" />
            <Typography variant="h6" gutterBottom color="success.main">
              Appeal Letter Generated Successfully!
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Your professional appeal letter has been generated and is ready for review.
              You can edit and customize it in the next step.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
              <DescriptionIcon size={16} />
              <Typography variant="body2">
                {generatedLetter?.content_format === 'html' ? 'Rich Text Format' : 'Markdown Format'}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon size={16} />}
              onClick={startGeneration}
              sx={{ mr: 2 }}
            >
              Regenerate Letter
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <AutoAwesomeIcon size={60} />
          <Typography variant="h6" gutterBottom>
            Ready to Generate Appeal Letter
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            Based on your uploaded documents and extracted information, our AI will create a
            professional appeal letter that addresses the denial reason and provides strong
            medical necessity arguments.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AutoAwesomeIcon size={16} />}
            onClick={startGeneration}
          >
            Generate Appeal Letter
          </Button>
        </CardContent>
      </Card>
    );
  };

  // Show loading state if appeal is not loaded yet
  if (!appeal || !appeal.id) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography>Loading appeal data...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Step 3: Generate Appeal Letter
      </Typography>

      <Typography variant="body1" color="textSecondary" paragraph>
        Review the information below and generate a professional appeal letter using AI.
      </Typography>

      {renderAppealSummary()}
      {renderModelSelection()}
      {renderGenerationInterface()}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          size="large"
          disabled={generating}
        >
          Back: Extract Information
        </Button>

        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!generationComplete}
          size="large"
        >
          Next: Review Letter
        </Button>
      </Box>
    </Box>
  );
};

export default GenerateStep;
